import type { ReactElement } from "react";

import { useOpenedArtifactStore } from "@virtual-brain-lib/frontend-design-system/components";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";
import type { ChatMessageWithUIState, SourceFile } from "@virtual-brain-lib/types";
import { getArtifactCardNeighbors } from "@virtual-brain-lib/utils-text-processing";

import ArtifactCard from "../../../ArtifactCard";
import { Markdown } from "../../../markdown";
import type { OpenArtifactParams } from "../../artifact-panel/useArtifactPanel";
import { useChatContext } from "../../core/context/ChatContext";

interface MessageContentProps {
	readonly children?: ReactElement;
	readonly message: ChatMessageWithUIState;
	readonly isMessageProcessing: boolean;
	readonly isAnyMessageProcessing?: boolean;
	readonly onSourceSelected?: (params: SourceFile) => void;
	readonly openArtifact?: (params: OpenArtifactParams) => void;
	readonly artifactCardBottomComponent?: (artifactId: string) => ReactElement | undefined;
}

export default function MessageContent({
	children,
	message,
	isMessageProcessing,
	isAnyMessageProcessing,
	onSourceSelected,
	openArtifact,
	artifactCardBottomComponent,
}: MessageContentProps) {
	const { type, botIcon, analyticsEventDomain } = useChatContext();
	const openedArtifact = useOpenedArtifactStore((store) => store.openedArtifact);

	function renderArtifact(id: string) {
		const artifact = message.artifacts?.find((a) => a.id === id);

		if (!artifact) {
			return null;
		}

		const isLoading = !artifact.name && Boolean(isMessageProcessing);
		const name = artifact.name?.length ? artifact.name : "Untitled Artifact";

		const bottomComponent =
			message.artifacts?.[0]?.id === id ? artifactCardBottomComponent?.(id) : undefined;

		const { hasTextBefore, hasTextAfter } = getArtifactCardNeighbors(message.content ?? "", id);

		const isArtifactCardDisabled = Boolean(isAnyMessageProcessing) && !isMessageProcessing;

		return (
			<div
				data-artifact-id={id}
				className={cn(hasTextBefore && "mt-4", hasTextAfter ? "mb-4" : "mb-3")}
			>
				<ArtifactCard
					name={name}
					isLoading={isLoading}
					isSelected={openedArtifact?.artifactId === id}
					isDisabled={isArtifactCardDisabled}
					disabledTooltip="Changing artifact is not possible while one is being generated"
					bottomComponent={bottomComponent}
					onClick={() => {
						openArtifact?.({
							artifactId: artifact.id,
							artifactName: artifact.name,
							assistantMessageId: message.id,
						});
					}}
				/>
			</div>
		);
	}

	if (children) {
		return children;
	}

	const markdown = (
		<Markdown
			key={message.contents?.length}
			contents={message.contents}
			isWriting={isMessageProcessing}
			renderArtifact={renderArtifact}
			analyticsEventDomain={analyticsEventDomain}
			onSourceSelected={onSourceSelected}
		>
			{message.content ?? ""}
		</Markdown>
	);

	if (type === "chatbot" && botIcon && message.role === "assistant") {
		return (
			<div className="flex gap-3">
				<img src={botIcon} alt="" className="h-8 w-8" />
				<div className="markdown-content">{markdown}</div>
			</div>
		);
	}

	return markdown;
}
